import { useMutation } from '@apollo/client/react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import React from 'react';
import { useNotify } from 'react-admin';

import {
  AdminCreateEligibleEmailAddressDocument,
  type AdminLaunchTestUserFragment,
  AdminRemoveEligibleEmailAddressDocument,
} from '../../../graphql/types';
import { Section } from './fields';

const errorMessage = (error: unknown) =>
  error instanceof Error ? error.message : String(error);

const looksLikeEmail = (value: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

/**
 * People added by hand so they can sign up before the file arrives. Adding
 * and removing saves right away, not on Save & continue.
 */
export const TestUsers: React.FC<{
  clientId: string;
  eligibleEmails: AdminLaunchTestUserFragment[];
  readOnly: boolean;
  onChanged: () => Promise<unknown>;
}> = ({ clientId, eligibleEmails, readOnly, onChanged }) => {
  const notify = useNotify();
  const [email, setEmail] = React.useState('');
  const [employeeType, setEmployeeType] = React.useState('');
  const [isStaff, setIsStaff] = React.useState(false);
  const [showError, setShowError] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [createEmail] = useMutation(AdminCreateEligibleEmailAddressDocument);
  const [removeEmail] = useMutation(AdminRemoveEligibleEmailAddressDocument);

  const trimmed = email.trim().toLowerCase();
  const invalid = !looksLikeEmail(trimmed);

  const run = async (action: () => Promise<unknown>, failure: string) => {
    setBusy(true);
    try {
      await action();
      await onChanged();
    } catch (error) {
      notify(`${failure}: ${errorMessage(error)}`, { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const handleAdd = async () => {
    // Enter in the email field can submit while the button is disabled.
    if (busy) {
      return;
    }
    if (invalid) {
      setShowError(true);
      return;
    }
    await run(async () => {
      await createEmail({
        variables: {
          input: {
            clientId,
            email: trimmed,
            isStaff,
            employeeType: employeeType.trim() || undefined,
          },
        },
      });
      setEmail('');
      setEmployeeType('');
      setIsStaff(false);
      setShowError(false);
    }, 'Failed to add the test user');
  };

  const handleRemove = (id: string) =>
    run(
      () => removeEmail({ variables: { id } }),
      'Failed to remove the test user',
    );

  const waiting = eligibleEmails.some((eligibleEmail) => !eligibleEmail.user);

  return (
    <Section
      title="Test users"
      hint="Add a few people by hand so they can sign up and try features before the full file arrives. File uploads never remove them. Each one is saved as soon as you add it."
    >
      {eligibleEmails.length > 0 && (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 560 }}>
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Staff</TableCell>
                <TableCell>Employee type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {eligibleEmails.map((eligibleEmail) => (
                <TableRow key={eligibleEmail.id}>
                  <TableCell sx={{ fontFamily: 'monospace' }}>
                    {eligibleEmail.email}
                  </TableCell>
                  <TableCell>{eligibleEmail.isStaff ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{eligibleEmail.employeeType || '—'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={
                        eligibleEmail.user ? 'Signed up' : 'Not signed up yet'
                      }
                      color={eligibleEmail.user ? 'success' : 'default'}
                      variant={eligibleEmail.user ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      aria-label={`Remove ${eligibleEmail.email}`}
                      disabled={readOnly || busy}
                      onClick={() => handleRemove(eligibleEmail.id)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
      {waiting && (
        <Box>
          <Button
            size="small"
            disabled={busy}
            onClick={() => run(onChanged, 'Failed to check sign-ups')}
          >
            Check sign-ups again
          </Button>
        </Box>
      )}
      {!readOnly && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ sm: 'flex-start' }}
        >
          <TextField
            size="small"
            label="Test user email"
            placeholder="name@company.com"
            type="email"
            value={email}
            error={showError && invalid}
            helperText={showError && invalid ? 'Enter an email address.' : ' '}
            onChange={(event) => setEmail(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleAdd();
              }
            }}
            sx={{ flex: 2 }}
          />
          <TextField
            size="small"
            label="Employee type"
            placeholder="Optional"
            value={employeeType}
            helperText=" "
            onChange={(event) => setEmployeeType(event.target.value)}
            sx={{ flex: 1 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={isStaff}
                onChange={(event) => setIsStaff(event.target.checked)}
              />
            }
            label="Staff"
          />
          <Button variant="outlined" disabled={busy} onClick={handleAdd}>
            Add test user
          </Button>
        </Stack>
      )}
    </Section>
  );
};
